import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
  downloadCostAnalysisForTask,
  downloadRoiReportForTask,
  downloadSupportResponseForTask,
  listQuoteFiles,
  renderQuoteForTask,
  type CostAnalysisTaskRequest,
  type DocumentFormat,
  type QuoteFileResponse,
  type QuoteRenderRequest,
  type RoiReportTaskRequest,
} from "../api/quoteDocuments";

export const QUOTE_FILES_KEY = "quote-files";

export function useDownloadCostAnalysis() {
  return useMutation<
    void,
    Error,
    { taskId: string; body?: CostAnalysisTaskRequest }
  >({
    mutationFn: ({ taskId, body }) => downloadCostAnalysisForTask(taskId, body),
  });
}

export function useDownloadRoiReport() {
  return useMutation<
    void,
    Error,
    { taskId: string; body?: RoiReportTaskRequest }
  >({
    mutationFn: ({ taskId, body }) => downloadRoiReportForTask(taskId, body),
  });
}

export function useDownloadSupportResponse() {
  return useMutation<void, Error, { taskId: string; format: DocumentFormat }>({
    mutationFn: ({ taskId, format }) =>
      downloadSupportResponseForTask(taskId, format),
  });
}

export function useRenderQuote() {
  const qc = useQueryClient();
  return useMutation<
    QuoteFileResponse,
    Error,
    { taskId: string; body?: QuoteRenderRequest }
  >({
    mutationFn: ({ taskId, body }) => renderQuoteForTask(taskId, body),
    onSuccess: (data) => {
      void qc.invalidateQueries({ queryKey: [QUOTE_FILES_KEY, data.taskId] });
    },
  });
}

export function useQuoteFiles(
  taskId: string | null | undefined,
  enabled = true,
) {
  return useQuery<QuoteFileResponse[]>({
    queryKey: [QUOTE_FILES_KEY, taskId],
    queryFn: () => listQuoteFiles(taskId as string),
    enabled: !!taskId && enabled,
    retry: false,
  });
}
